/* eslint-disable react/prop-types */
import Tooltip from "@shared/v2/Tooltip";
import IconButton from "@shared/v2/IconButton";
import { BoxArchiveSolidV6, PencilSolidV6, TrashSolidV6 } from "@shared/v2/Icomoon";

import ImageCell from "./shared/ImageCell";
import AgentCell from "./shared/AgentCell";
import TextCell from "./shared/TextCell";
import TypeCell from "./shared/TypeCell";
import CurrencyCell from "./shared/CurrencyCell";
import DateCell from "./shared/DateCell";
import TDIOptionsStatusCell from "./TDIOptionsStatusCell";
import OptionsInlineCell from "./shared/OptionsInlineCell";
import FadeOut from "./shared/FadeOut";
import { Listing } from "../models";
import { dynamicRow } from "../utils/dynamicRow";
import { currentFiltersAsString } from "../utils/urlHelpers";
import { isEditableAt } from "../utils/helpers";
import ThumbnailIcon from "./shared/ThumbnailIcon";
import { isCustomTitle } from "../utils/statuses";

const widths = {
  minWidth: "150px",
  maxWidth: "300px",
};
const Transaction = ({
  listing,
  sources,
  updateListing,
  archiveListing,
  deleteListing,
  renderableColumns,
  permissions,
}) => {
  const onChange = (key) => (value) => updateListing(new Listing({ ...listing, [key]: value }));
  const canDeleteListing = permissions.listings.delete && listing.status === "archived";
  const canArchiveListing = !listing.status === "archived";
  const canEditFinancials = false;
  const editableCheck = (property) => isEditableAt(listing, property, canEditFinancials);
  const searchString = currentFiltersAsString();
  const baseColumns = dynamicRow(renderableColumns, [
    [
      "photo",
      <td className="tw-whitespace-nowrap tw-relative" data-cy="transaction-item-photo">
        <div className="tw-flex tw-w-[80px] tw-justify-center tw-items-center">
          {listing.photoURL ? (
            <ImageCell value={listing.photoURL} isEditable={editableCheck("photo")} ratio="wide" />
          ) : (
            <ThumbnailIcon transactionType={listing.type} size="xl" />
          )}
        </div>
      </td>,
    ],
    [
      "address",
      <td
        className="tw-relative tw-border tw-border-t-0 tw-border-b-0 tw-border-l-0 tw-border-r-1px tw-border-gray-light tw-border-solid tw-px-28px tw-text-left tw-overflow-hidden"
        style={widths}
        data-cy="transaction-item-addressTitle"
      >
        <a
          className="tw-no-underline tw-block tw-max-h-38px tw-overflow-hidden"
          href={`${listing.showURL}?${searchString}`}
        >
          {isCustomTitle(listing.type, listing.useCustomTitle) ? (
            <p className="tw-text-gray-75 tw-m-0 tw-truncate tw-text-ellipsis">{listing.title}</p>
          ) : (
            listing.address.fullAddress
              .filter((line) => !!line)
              .map((line) => <p className="tw-text-gray-75 tw-m-0 tw-truncate tw-text-ellipsis">{line}</p>)
          )}
        </a>
        <FadeOut />
      </td>,
    ],
    [
      "type",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td className="tw-whitespace-nowrap tw-relative tw-px-28px" data-cy="transaction-item-type">
        <TypeCell value={listing.type} />
      </td>,
    ],
    [
      "status",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td className="tw-whitespace-nowrap tw-relative tw-px-28px" data-cy="transaction-item-status">
        <TDIOptionsStatusCell listing={listing} />
      </td>,
    ],
    [
      "mls",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-mlsNumber"
      >
        <TextCell
          value={listing.mlsNumber}
          isEditable={editableCheck("mls")}
          onChange={onChange("mlsNumber")}
        />
      </td>,
    ],
    [
      "agent",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td className="tw-whitespace-nowrap tw-relative tw-px-28px" data-cy="transaction-item-agent">
        <AgentCell agent={listing.agent} size="small" showName rounded isEditable={editableCheck("agent")} />
      </td>,
    ],
    [
      "dateListed",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-dateListed"
      >
        <DateCell
          value={listing.dateListed}
          isEditable={editableCheck("dateListed")}
          onChange={onChange("dateListed")}
        />
      </td>,
    ],
    [
      "price",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-listingPrice"
      >
        <CurrencyCell
          value={listing.listingPrice}
          isEditable={editableCheck("price")}
          onChange={onChange("listingPrice")}
        />
        <FadeOut />
      </td>,
    ],
    [
      "expiration",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-dateExpired"
      >
        <DateCell
          value={listing.dateExpired}
          isEditable={editableCheck("expiration")}
          onChange={onChange("dateExpired")}
        />
      </td>,
    ],
    [
      "closePrice",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-closePrice"
      >
        <CurrencyCell value={listing.closePrice} />
        <FadeOut />
      </td>,
    ],
    [
      "gci",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-gci"
      >
        <CurrencyCell value={listing.grossCommission} />
        <FadeOut />
      </td>,
    ],
    [
      "closeDate",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-closeDate"
      >
        <DateCell
          value={listing.closeDate}
          isEditable={editableCheck("closeDate")}
          onChange={onChange("closeDate")}
        />
      </td>,
    ],
    [
      "dom",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-dom"
      >
        <TextCell value={listing.dom} isEditable={editableCheck("dom")} />
      </td>,
    ],
    [
      "dateCanceled",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-dateCanceled"
      >
        <DateCell
          value={listing.canceledDate}
          isEditable={editableCheck("dateCanceled")}
          onChange={onChange("canceledDate")}
        />
      </td>,
    ],
    [
      "mutualAcceptanceDate",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-mutualAcceptanceDate"
      >
        <DateCell value={listing.mutualAcceptanceDate} isEditable={editableCheck("mutualAcceptanceDate")} />
      </td>,
    ],
    [
      "lastUpdated",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-lastUpdated"
      >
        <DateCell value={listing.lastUpdated} isEditable={editableCheck("lastUpdated")} />
      </td>,
    ],
    [
      "source",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-sourceId"
      >
        <OptionsInlineCell
          isEditable={editableCheck("source")}
          optionsList={sources}
          selectedId={listing.sourceId}
          onUpdateOption={onChange("sourceId")}
          selectionWrapperClassName="!tw-z-[100000000]"
          rowClassName="tw-w-max selected-option tw-cursor-pointer tw-flex tw-flex-row tw-flex-nowrap tw-items-center tw-gap-[8px] tw-text-center tw-transition tw-duration-200"
          iconClassName=""
        />
      </td>,
    ],
    [
      "statusChangedAt",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-statusChangedAt"
      >
        <DateCell value={listing.statusChangedAt} isEditable={editableCheck("statusChangedAt")} />
      </td>,
    ],
    [
      "depositDue",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-depositDue"
      >
        <DateCell
          value={listing.depositDue}
          isEditable={editableCheck("depositDue")}
          onChange={onChange("depositDue")}
        />
      </td>,
    ],
    [
      "additionalDepositDue",
      // eslint-disable-next-line jsx-a11y/control-has-associated-label
      <td
        className="tw-whitespace-nowrap tw-relative tw-px-28px"
        style={widths}
        data-cy="transaction-item-additionalDepositDue"
      >
        <DateCell
          value={listing.additionalDepositDue}
          isEditable={editableCheck("additionalDepositDue")}
          onChange={onChange("additionalDepositDue")}
        />
      </td>,
    ],
    ...Object.keys(listing.newMilestones)
      .filter((key) => renderableColumns[key]?.isNewMilestone)
      .map((key) => [
        key,
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        <td
          className="tw-whitespace-nowrap tw-relative tw-px-28px"
          style={widths}
          data-cy="transaction-item-newMilestone"
        >
          <DateCell value={listing.newMilestones[key]?.date} isEditable={editableCheck("newMilestones")} />
        </td>,
      ]),
  ]);

  return (
    <tr
      className="transactions__transaction transactions__row tw-border tw-border-l-0 tw-border-t-1px tw-border-gray-light tw-border-solid tw-h-58px tw-text-gray-75"
      key={JSON.stringify(listing)}
      data-listing-id={listing.id}
      data-listing-type={listing.type}
      data-cy="transaction-item-list-view"
    >
      {baseColumns}
      <td className="tw-relative tw-px-28px">
        <div className=" tw-flex tw-justify-start tw-items-center tw-gap-[4px]">
          <a href={listing.editURL} className="tw-pointer">
            <Tooltip
              trigger={
                <IconButton size="small" schema="transparent">
                  <PencilSolidV6 />
                </IconButton>
              }
              content="Edit"
            />
          </a>
          {canDeleteListing && (
            <Tooltip
              trigger={
                <IconButton size="small" schema="transparent" onClick={() => deleteListing(listing)}>
                  <TrashSolidV6 className="tw-text-semantic-orange-50" />
                </IconButton>
              }
              content="Delete"
            />
          )}
          {canArchiveListing && (
            <Tooltip
              trigger={
                <IconButton size="small" schema="transparent" onClick={() => archiveListing(listing)}>
                  <BoxArchiveSolidV6 />
                </IconButton>
              }
              content="Archive"
            />
          )}
        </div>
      </td>
    </tr>
  );
};

export default Transaction;
